import {isUserProcessActive, UserProcess} from "@ots-frontend/ots-data";
import {OtsBadgeTone} from "../components/OtsBadge";

/**
 * Words the person sees for processes. The front-end name for "processes" isn't settled, so it lives here and
 * nowhere else.
 */
export const PROCESS_COPY = {
  trayLabel: "Processes",
  panelTitle: "Your processes",
  emptyTitle: "Nothing running",
  emptyDescription: "Reports, merges and exports you start appear here. You can leave the screen while they run.",
  allKinds: "All",
  markAllSeen: "Mark all seen",
  liveUpdatesPaused: "Live updates paused. Retrying…",
  loadFailed: "Your processes couldn't be loaded.",
} as const;

/**
 * Groups of processes, from the server's process type, for an icon on each row and the tray's filter. Reports
 * produce something to read; merges and data actions change records; imports and exports move data in and out.
 */
export type ProcessKind = "report" | "merge" | "dataAction" | "export" | "import" | "other";

export type ProcessKindInfo = {
  kind: ProcessKind;
  /** One of these, e.g. "Report". */
  label: string;
  /** The filter's name for the group, e.g. "Reports". */
  pluralLabel: string;
};

/** In filter order. */
export const PROCESS_KINDS: readonly ProcessKindInfo[] = [
  {kind: "report", label: "Report", pluralLabel: "Reports"},
  {kind: "merge", label: "Merge", pluralLabel: "Merges"},
  {kind: "dataAction", label: "Data action", pluralLabel: "Data actions"},
  {kind: "export", label: "Export", pluralLabel: "Exports"},
  {kind: "import", label: "Import", pluralLabel: "Imports"},
  {kind: "other", label: "Process", pluralLabel: "Other"},
];

const kindsByType: Record<string, ProcessKind> = {
  REPORT: "report",
  MERGE: "merge",
  DATA_ACTION: "dataAction",
  EXPORT: "export",
  IMPORT: "import",
};

/** The group a process belongs to; types this client doesn't know yet are "other". */
export const processKind = (process: Pick<UserProcess, "type">): ProcessKind => kindsByType[process.type] ?? "other";

export const processKindInfo = (kind: ProcessKind): ProcessKindInfo =>
  PROCESS_KINDS.find((info) => info.kind === kind) ?? PROCESS_KINDS[PROCESS_KINDS.length - 1];

export type ProcessKindCount = ProcessKindInfo & {count: number};

/** The kinds in this list with how many of each, in {@link PROCESS_KINDS} order; kinds with none are left out. */
export const countProcessKinds = (processes: Pick<UserProcess, "type">[]): ProcessKindCount[] => {
  const counts = new Map<ProcessKind, number>();
  processes.forEach((process) => {
    const kind = processKind(process);
    counts.set(kind, (counts.get(kind) ?? 0) + 1);
  });
  return PROCESS_KINDS.filter((info) => counts.has(info.kind))
    .map((info) => ({...info, count: counts.get(info.kind) ?? 0}));
};

const statusTones: Record<string, OtsBadgeTone> = {
  QUEUED: "neutral",
  RUNNING: "info",
  SUCCEEDED: "success",
  PARTIAL: "warning",
  FAILED: "danger",
  CANCELED: "neutral",
};

const statusLabels: Record<string, string> = {
  QUEUED: "Queued",
  RUNNING: "Running",
  SUCCEEDED: "Done",
  PARTIAL: "Finished with problems",
  FAILED: "Failed",
  CANCELED: "Canceled",
};

export const processStatusTone = (status: string): OtsBadgeTone => statusTones[status] ?? "neutral";

export const processStatusLabel = (status: string): string => statusLabels[status] ?? status;

/** Needs the person's attention: it failed or only partly worked. */
export const isProcessProblem = (process: Pick<UserProcess, "status">) =>
  process.status === "FAILED" || process.status === "PARTIAL";

export type ProcessTrayState = "empty" | "processing" | "complete" | "attention";

export type ProcessTraySummary = {
  state: ProcessTrayState;
  /** Queued plus running. */
  activeCount: number;
  /** Finished processes the person hasn't seen. */
  unseenCount: number;
  /** An unseen finished process failed or partly worked; shown even while others run. */
  unseenProblem: boolean;
};

/**
 * What the navbar trigger shows. Running work wins (spinner and count); otherwise an unseen problem (red dot)
 * beats unseen successes (green dot); otherwise it's quiet.
 */
export const summarizeProcesses = (processes: UserProcess[]): ProcessTraySummary => {
  const activeCount = processes.filter(isUserProcessActive).length;
  const unseen = processes.filter((process) => !isUserProcessActive(process) && !process.seen);
  const unseenProblem = unseen.some(isProcessProblem);

  const state: ProcessTrayState = activeCount > 0 ? "processing"
    : unseenProblem ? "attention"
      : unseen.length > 0 ? "complete"
        : "empty";

  return {state, activeCount, unseenCount: unseen.length, unseenProblem};
};

/** Active first (oldest started first, as they'll finish first), then finished, newest first. */
export const sortProcesses = (processes: UserProcess[]): UserProcess[] => {
  const active = processes.filter(isUserProcessActive)
    .sort((a, b) => (a.createDttm ?? 0) - (b.createDttm ?? 0));
  const finished = processes.filter((process) => !isUserProcessActive(process))
    .sort((a, b) => (b.endDttm ?? b.createDttm ?? 0) - (a.endDttm ?? a.createDttm ?? 0));
  return [...active, ...finished];
};

/** "42s", "1m 42s", "2h 5m". */
export const formatProcessDuration = (startDttm?: number, endDttm?: number): string | undefined => {
  if (startDttm === undefined || endDttm === undefined || endDttm < startDttm) return undefined;

  const seconds = Math.floor((endDttm - startDttm) / 1_000);
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${Math.max(1, seconds)}s`;
};

/** "184 KB", "2.5 MB". */
export const formatFileSize = (bytes?: number): string | undefined => {
  if (bytes === undefined) return undefined;
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_024 * 1_024) return `${Math.round(bytes / 1_024)} KB`;
  return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`;
};

/** "3 of 12", or undefined when the process hasn't said how much work there is. */
export const formatProcessProgress = (process: Pick<UserProcess, "progressDone" | "progressTotal">) =>
  process.progressTotal !== undefined && process.progressTotal > 0
    ? `${process.progressDone ?? 0} of ${process.progressTotal}`
    : undefined;

/**
 * A process that just finished since the previous snapshot, for a one-time notice. Ignores processes that were
 * already finished the first time they were seen, so a page load doesn't replay old results.
 */
export const findNewlyFinished = (previous: UserProcess[], next: UserProcess[]): UserProcess[] => {
  const before = new Map(previous.map((process) => [process.id, process]));
  return next.filter((process) => {
    const earlier = before.get(process.id);
    return earlier !== undefined && isUserProcessActive(earlier) && !isUserProcessActive(process);
  });
};
